<script setup lang="ts">
/**
 * Knowledge maintenance: scan for chores (duplicates, noise, broken
 * relations, empty/duplicate documents, tag variants), see the ones worth
 * fixing first — review-only kinds (orphans, rare tags, cross-type names,
 * stale pages) behind a filter —, compare duplicates side by side, preview
 * what applying would change (dry run), apply as a job and revert the
 * resulting change sets. Duplicates whose merge was reverted before — or
 * whose history could not be read in full — are flagged, never selected for
 * you, and need a tick in the preview.
 */
import { AlertTriangle, ArrowLeft, ArrowRight, Check, ChevronDown, Eye, Loader2, Play, RotateCcw, Undo2, Wrench, X } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { refDebounced } from "@vueuse/core";
import { computed, h, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";
import type { MaintenanceApplyResult, MaintenanceFinding, MaintenanceKind, MaintenancePlan } from "@/api/contracts";
import { useJob } from "@/api/jobs";
import { applyFindings, dismissFindings, planFindings, reopenFindings, revertChangeSet, startMaintenanceScan, useMaintenanceSummary, type MaintenanceFilter } from "@/api/knowledge";
import DataTable from "@/components/common/DataTable.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import RiskBadge from "@/components/common/RiskBadge.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import ConfidenceBadge from "@/components/knowledge/ConfidenceBadge.vue";
import KnowledgeNav from "@/components/knowledge/KnowledgeNav.vue";
import MaintenanceFindingCard from "@/components/knowledge/MaintenanceFindingCard.vue";
import {
  applyBlocked, autoSelectable as selectableFor, FIX_ORDER, isReviewOnly, KIND_INFO, planMergeChecks, REVIEW_ORDER, useFindings, useRevertedMerges, useTopFindings,
} from "@/components/knowledge/maintenanceKinds";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { confirm } from "@/composables/useConfirm";
import { t } from "@/copy/strings";
import { describeCron } from "@/lib/cron";
import { errorMessage } from "@/lib/errors";
import { formatDate, formatNumber, humanize } from "@/lib/format";
import type { Column } from "@/lib/table";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Knowledge maintenance", requires: { capability: "knowledge.admin" } } });

const queryClient = useQueryClient();

// --- Summary and scans -------------------------------------------------------------------------------
const summary = useMaintenanceSummary();
const scanJobId = ref<string | null>(null);
const scanJob = useJob(scanJobId);
const scanning = computed(() => Boolean(scanJobId.value) || Boolean(summary.data.value?.running));
const lastScan = computed(() => summary.data.value?.scans.find((s) => s.status !== "running") ?? null);

watch(
  () => summary.data.value?.running?.jobId,
  (id) => {
    if (id && !scanJobId.value) scanJobId.value = id;
  },
  { immediate: true },
);
watch(
  () => scanJob.data.value?.status,
  async (status) => {
    if (!status || !["completed", "failed", "cancelled"].includes(status)) return;
    if (status === "completed") toast.success("Scan finished.");
    else toast.error(`The scan ${status}: ${scanJob.data.value?.statusMessage ?? ""}`);
    scanJobId.value = null;
    await refresh();
  },
);

async function scan() {
  try {
    const r = await startMaintenanceScan();
    scanJobId.value = r.jobId;
    toast.info(r.started ? "Scanning the knowledge base…" : "A scan is already running.");
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function refresh() {
  await queryClient.invalidateQueries({ queryKey: ["knowledge"] });
}

// --- Kinds --------------------------------------------------------------------------------------------
const byKind = computed(() => new Map((summary.data.value?.byKind ?? []).map((k) => [k.kind, k])));
const countOf = (k: MaintenanceKind) => byKind.value.get(k)?.count ?? 0;
const labelOf = (k: MaintenanceKind) => byKind.value.get(k)?.label ?? humanize(k);
const fixKinds = computed(() => FIX_ORDER.filter((k) => countOf(k) > 0));
const reviewKinds = computed(() => REVIEW_ORDER.filter((k) => countOf(k) > 0));
const reviewTotal = computed(() => reviewKinds.value.reduce((n, k) => n + countOf(k), 0));

// --- Filters (deep link: /knowledge/maintenance?kind=duplicate) ---------------------------------------
const initialKind = useRoute().query.kind;
const kind = ref<MaintenanceKind | undefined>(typeof initialKind === "string" && initialKind in KIND_INFO ? (initialKind as MaintenanceKind) : undefined);
const status = ref<MaintenanceFinding["status"]>("open");
const search = ref("");
const q = refDebounced(search, 300);
const showReview = ref(kind.value ? isReviewOnly(kind.value) : false);
/** Open findings, no kind, no search: the overview of chores per kind. */
const overview = computed(() => status.value === "open" && !kind.value && !q.value.trim());
const filter = computed<MaintenanceFilter>(() => ({ kind: kind.value, status: status.value, q: q.value || undefined }));

const kindOptions = computed(() => {
  const kinds = [...fixKinds.value, ...(showReview.value ? reviewKinds.value : [])];
  if (kind.value && !kinds.includes(kind.value)) kinds.push(kind.value);
  return kinds.map((k) => ({ value: k, label: labelOf(k), count: countOf(k) }));
});
const statusOptions = [
  { value: "open", label: "Open" },
  { value: "applied", label: "Applied" },
  { value: "dismissed", label: "Dismissed" },
  { value: "resolved", label: "Resolved by later scans" },
];

function openKind(k: MaintenanceKind) {
  if (isReviewOnly(k)) showReview.value = true;
  kind.value = k;
  window.scrollTo({ top: 0, behavior: "smooth" });
}

const findings = useFindings(filter, () => !overview.value);
const items = computed(() => findings.data.value?.pages.flatMap((p) => p.items) ?? []);
const top = useTopFindings(fixKinds, overview);
/** Up to three findings with a fix to apply, per kind. */
const topFixes = computed(() => new Map([...top.value].map(([k, v]) => [k, v.items.filter((f) => f.action).slice(0, 3)])));
/** The overview lists kinds with something to apply; the rest of their kind (all review only) joins "Review only". */
const fixShown = computed(() => fixKinds.value.filter((k) => top.value.get(k)?.pending || (topFixes.value.get(k)?.length ?? 0) > 0));
const reviewShown = computed(() => [...fixKinds.value.filter((k) => !fixShown.value.includes(k)), ...reviewKinds.value]);
const reviewShownTotal = computed(() => reviewShown.value.reduce((n, k) => n + countOf(k), 0));
/** "Review only" as a table: open a kind to look at its findings. */
type ReviewRow = { kind: MaintenanceKind; label: string; count: number; effect: string };
const reviewRows = computed<ReviewRow[]>(() => reviewShown.value.map((k) => ({ kind: k, label: labelOf(k), count: countOf(k), effect: KIND_INFO[k].effect })));
const reviewColumns: Column<ReviewRow>[] = [
  { id: "label", header: "Kind", accessorFn: (r) => r.label, meta: { mobile: "title" }, cell: ({ row }) => h("span", { class: "font-medium whitespace-nowrap" }, row.original.label) },
  // TableCell is nowrap: the sentence wraps so the Findings count stays on screen.
  { id: "effect", header: "What it means", accessorFn: (r) => r.effect, enableSorting: false, meta: { mobile: "subtitle" }, cell: ({ row }) => h("p", { class: "min-w-64 whitespace-normal text-pretty text-muted-foreground" }, row.original.effect) },
  { id: "count", header: "Findings", accessorFn: (r) => r.count, meta: { align: "end", mobile: "badge" }, cell: ({ row }) => h("span", { class: "font-medium tabular-nums" }, formatNumber(row.original.count)) },
];
const topItems = computed(() => [...topFixes.value.values()].flat());
const shown = computed(() => (overview.value ? topItems.value : items.value));
const { checks, reverted, unknown, checking } = useRevertedMerges(shown);

// --- Selection (one kind at a time) and survivor choices ---------------------------------------------
const selected = reactive(new Set<string>());
/** Duplicates: which entity survives (default: the one the scan proposes). */
const survivor = reactive<Record<string, string>>({});
watch(filter, () => selected.clear());

const canSelect = computed(() => Boolean(kind.value) && status.value === "open");
const actionable = computed(() => items.value.filter((f) => f.status === "open" && f.action));
/** "Select all" never picks a merge that was reverted before or could not be checked. */
const autoSelectable = computed(() => selectableFor(actionable.value, checks.value));
const revertedShown = computed(() => actionable.value.filter((f) => reverted.value.has(f.id)).length);
const uncheckedShown = computed(() => actionable.value.filter((f) => unknown.value.has(f.id)).length);
const allSelected = computed(() => autoSelectable.value.length > 0 && autoSelectable.value.every((f) => selected.has(f.id)));
function toggleAll(on: boolean) {
  for (const f of autoSelectable.value) {
    if (on) selected.add(f.id);
    else selected.delete(f.id);
  }
}
function toggle(id: string, on: boolean) {
  if (on) selected.add(id);
  else selected.delete(id);
}

const findingById = computed(() => new Map(shown.value.map((f) => [f.id, f])));
function intoOf(f: MaintenanceFinding): string | undefined {
  return f.action?.op === "merge" ? (survivor[f.id] ?? f.action.into) : undefined;
}
const choicesFor = (ids: string[]) => Object.fromEntries(ids.flatMap((id) => (survivor[id] ? [[id, { into: survivor[id] }]] : [])));

function describe(f: MaintenanceFinding): string {
  const a = f.action;
  if (!a) return "Review only";
  const name = (id: string) => f.entities.find((e) => e.id === id)?.name ?? "?";
  switch (a.op) {
    case "merge": {
      const into = intoOf(f)!;
      const from = into === a.into ? a.from : a.into;
      return `Merge “${name(from)}” into “${name(into)}”`;
    }
    case "archive":
      return `Archive “${name(a.entity)}”`;
    case "delete":
      return `Delete “${name(a.entity)}”`;
    case "unrelate":
      return `Remove the relation ${a.predicate.replace(/_/g, " ")}`;
    case "relink":
      return `Re-point “${a.predicate.replace(/_/g, " ")} ${name(a.from)}” to “${name(a.into)}”`;
    case "rename_tag":
      return `${a.from.map((x) => `#${x}`).join(", ")} → #${a.to}`;
    case "remove_document":
      return `Remove the upload “${f.documents.find((d) => d.id === a.document)?.title ?? "?"}”`;
    case "suggest_relations":
      return summary.data.value?.ai.suggestions ? "Ask AI for relations (suggestions go to the inbox)" : "Suggest relations (needs an AI model)";
  }
}

// --- Dismiss / reopen ----------------------------------------------------------------------------------
async function dismiss(ids: string[], label = "Dismiss") {
  const answer = await confirm({
    title: ids.length === 1 ? `${label}?` : `Dismiss ${ids.length} findings?`,
    description: "Later scans will not suggest them again (you can reopen them under “Dismissed”).",
    confirmLabel: label,
    reason: { label: "Why? (optional)", minLength: 0 },
  });
  if (!answer) return;
  try {
    const r = await dismissFindings(ids, answer.reason || undefined);
    toast.success(`Dismissed ${r.dismissed}.`);
    for (const id of ids) selected.delete(id);
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function reopen(id: string) {
  try {
    await reopenFindings([id]);
    toast.success("Reopened.");
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

// --- Preview (dry run) and apply ------------------------------------------------------------------------
const plan = ref<MaintenancePlan | null>(null);
const planIds = ref<string[]>([]);
const planning = ref(false);
const planOpen = ref(false);
const applyReason = ref("");
const repeatOk = ref(false);
const applyJobId = ref<string | null>(null);
const applyJob = useJob(applyJobId);
const result = ref<MaintenanceApplyResult | null>(null);
const titles = computed(() => new Map(shown.value.map((f) => [f.id, f.title])));
const planErrors = computed(() => plan.value?.groups.reduce((n, g) => n + g.errors.length, 0) ?? 0);
const planApplicable = computed(() => (plan.value?.groups.reduce((n, g) => n + g.items.length, 0) ?? 0) - planErrors.value);
/** Planned merges that were reverted before, or could not be checked: applying needs an explicit tick. */
const planMerges = computed(() => (plan.value ? planMergeChecks(plan.value, checks.value) : { reverted: [], unknown: [], pending: [] }));
const planRepeats = computed(() => planMerges.value.reverted);
const planUnchecked = computed(() => planMerges.value.unknown);
const planHasMerges = computed(() => plan.value?.groups.some((g) => g.op === "merge") ?? false);
const blocked = computed(() => applyBlocked({ applicable: planApplicable.value, merges: planMerges.value, repeatOk: repeatOk.value }));

async function preview(ids: string[]) {
  if (!ids.length) return;
  planning.value = true;
  try {
    planIds.value = ids;
    applyReason.value = "";
    repeatOk.value = false;
    plan.value = await planFindings(ids, choicesFor(ids));
    planOpen.value = true;
  } catch (err) {
    toast.error(errorMessage(err));
  } finally {
    planning.value = false;
  }
}

async function apply() {
  if (!plan.value || blocked.value) return;
  try {
    const r = await applyFindings(planIds.value, choicesFor(planIds.value), plan.value.token, applyReason.value.trim() || undefined);
    applyJobId.value = r.jobId;
    planOpen.value = false;
    result.value = null;
    toast.info("Applying…");
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

watch(
  () => applyJob.data.value?.status,
  async (s) => {
    if (!s || !["completed", "failed", "cancelled"].includes(s)) return;
    if (s === "completed") {
      result.value = (applyJob.data.value?.result ?? null) as MaintenanceApplyResult | null;
      toast.success(`Done: ${result.value?.applied ?? 0} applied${result.value?.failed ? `, ${result.value.failed} failed` : ""}.`);
    } else toast.error(`Applying ${s}: ${applyJob.data.value?.statusMessage ?? ""}`);
    applyJobId.value = null;
    selected.clear();
    await refresh();
  },
);

async function revert(changeSetId: string) {
  const answer = await confirm({ title: t.knowledge.revertTitle, description: "Undo this maintenance change set. Later edits to the same pages are kept where possible.", confirmLabel: t.knowledge.revert, destructive: true, reason: true });
  if (!answer) return;
  try {
    const r = await revertChangeSet(changeSetId, answer.reason);
    if (r.conflicts.length) toast.warning(`Reverted with ${r.conflicts.length} conflict(s).`);
    else toast.success("Reverted.");
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}
</script>

<template>
  <div>
    <PageHeader :title="t.knowledge.maintenance" :description="t.knowledge.maintenanceDescription">
      <template #actions>
        <Button :disabled="scanning" @click="scan">
          <Loader2 v-if="scanning" class="animate-spin" aria-hidden="true" /><Play v-else /> {{ scanning ? "Scanning…" : "Scan now" }}
        </Button>
      </template>
    </PageHeader>
    <KnowledgeNav />

    <!-- Scan status -->
    <div class="surface-inset mb-4 flex flex-col gap-1 px-4 py-3 text-sm sm:flex-row sm:items-center sm:gap-4" role="status">
      <template v-if="scanning">
        <span class="inline-flex items-center gap-2"><Loader2 class="size-4 animate-spin" aria-hidden="true" /> {{ scanJob.data.value?.statusMessage ?? "Scanning…" }}</span>
        <span v-if="scanJob.data.value?.progress" class="tabular-nums text-muted-foreground">{{ Math.round((scanJob.data.value.progress ?? 0) * 100) }}%</span>
      </template>
      <span v-else-if="lastScan">
        Last scan <RelativeTime :value="lastScan.finishedAt ?? lastScan.startedAt" /> ({{ lastScan.trigger === "schedule" ? "scheduled" : `by ${lastScan.requestedBy ?? "someone"}` }}<template v-if="lastScan.status === 'failed'">, failed: {{ lastScan.error }}</template>)
      </span>
      <span v-else class="text-muted-foreground">No scan yet. Start one — it only reads; nothing changes until you apply findings.</span>
      <span class="text-muted-foreground sm:ml-auto">
        <template v-if="summary.data.value?.schedule.enabled"><span :title="`UTC cron ${summary.data.value.schedule.cron}`">Scheduled scans: {{ describeCron(summary.data.value.schedule.cron) }}</span></template>
        <template v-else-if="summary.data.value">Scheduled scans are off</template>
        <template v-if="summary.data.value"> · AI {{ summary.data.value.ai.available && summary.data.value.ai.judgement ? "double-checks noise and duplicates" : "not used" }}</template>
        · <RouterLink to="/admin/settings" class="text-primary hover:underline">Settings</RouterLink>
      </span>
    </div>

    <!-- Applying -->
    <div v-if="applyJobId" class="surface-inset mb-4 flex flex-col gap-2 px-4 py-3 text-sm" role="status">
      <div class="flex items-center gap-3">
        <Loader2 class="size-4 animate-spin" aria-hidden="true" />
        <span>{{ applyJob.data.value?.statusMessage ?? "Applying…" }}</span>
        <span class="ml-auto tabular-nums text-muted-foreground">{{ Math.round((applyJob.data.value?.progress ?? 0) * 100) }}%</span>
      </div>
      <div class="h-1.5 w-full overflow-hidden rounded-full bg-primary/15"><div class="h-full bg-primary transition-[width]" :style="{ width: `${Math.round((applyJob.data.value?.progress ?? 0) * 100)}%` }" /></div>
    </div>

    <!-- Result of the last apply -->
    <section v-if="result" class="surface mb-6 p-4 sm:p-5" aria-labelledby="result-title">
      <div class="mb-2 flex items-center gap-2">
        <h2 id="result-title" class="section-title">Applied {{ result.applied }}<template v-if="result.failed">, {{ result.failed }} failed</template></h2>
        <Button variant="ghost" size="sm" class="ml-auto" @click="result = null"><X /> Close</Button>
      </div>
      <ul class="flex flex-col gap-3 text-sm">
        <li v-for="g in result.groups" :key="g.op + g.reason">
          <p class="font-medium">{{ g.reason }}</p>
          <p class="text-muted-foreground">{{ g.applied }} applied<template v-if="g.failed.length">, {{ g.failed.length }} failed</template></p>
          <ul v-if="g.failed.length" class="mt-1 list-disc pl-5 text-destructive">
            <li v-for="x in g.failed" :key="x.findingId">{{ titles.get(x.findingId) ?? `#${x.findingId}` }}: {{ x.error }}</li>
          </ul>
          <div v-if="g.changeSetIds.length" class="mt-1 flex flex-wrap items-center gap-2">
            <span class="text-xs text-muted-foreground">Change sets:</span>
            <span v-for="id in g.changeSetIds" :key="id" class="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs">
              <code>{{ id.slice(0, 8) }}</code>
              <button type="button" class="inline-flex cursor-pointer items-center gap-1 text-primary hover:underline" @click="revert(id)"><RotateCcw class="size-3" aria-hidden="true" /> Revert</button>
            </span>
            <RouterLink to="/knowledge/changes" class="text-xs text-primary hover:underline">Change log</RouterLink>
          </div>
        </li>
      </ul>
    </section>

    <!-- Filters -->
    <div class="mb-4 flex flex-col gap-3">
      <div class="flex flex-col gap-2 sm:flex-row sm:items-start">
        <Button v-if="kind" variant="ghost" size="sm" class="self-start" @click="kind = undefined"><ArrowLeft /> All chores</Button>
        <FilterChips v-if="kindOptions.length" v-model="kind" :options="kindOptions" label="Kind" />
      </div>
      <div class="grid gap-2 sm:grid-cols-3">
        <SimpleSelect v-model="status" :options="statusOptions" aria-label="Status" />
        <Input v-model="search" type="search" placeholder="Search findings…" aria-label="Search findings" class="sm:col-span-2" />
      </div>
      <button
        v-if="reviewKinds.length && !(kind && isReviewOnly(kind))"
        type="button"
        class="inline-flex cursor-pointer items-center gap-1 self-start text-left text-sm text-muted-foreground hover:text-foreground"
        :aria-expanded="showReview"
        @click="showReview = !showReview"
      >
        <ChevronDown :class="cn('size-4 transition-transform', showReview && 'rotate-180')" aria-hidden="true" />
        {{ showReview ? "Hide" : "Show" }} review-only kinds ({{ formatNumber(reviewTotal) }}: {{ reviewKinds.map(labelOf).join(", ").toLowerCase() }})
      </button>
      <div v-if="kind" class="surface-inset flex flex-col gap-1 px-4 py-3 text-sm sm:flex-row sm:items-center sm:gap-3">
        <p class="min-w-0 flex-1">{{ KIND_INFO[kind].effect }}</p>
        <RiskBadge v-if="KIND_INFO[kind].group === 'fix'" :risk="KIND_INFO[kind].risk" class="self-start sm:self-center" />
        <span v-else class="self-start text-xs text-muted-foreground sm:self-center">Review only</span>
      </div>
    </div>

    <!-- Overview: chores to fix, per kind -->
    <template v-if="overview">
      <ListSkeleton v-if="summary.isPending.value" :rows="3" />
      <ErrorState v-else-if="summary.error.value" :error="summary.error.value" @retry="summary.refetch()" />
      <template v-else>
        <EmptyState
          v-if="!fixShown.length"
          :icon="Wrench"
          :title="lastScan ? 'Nothing to fix' : 'No findings yet'"
          :description="lastScan ? (reviewShownTotal ? 'Only findings to review are open (see below).' : 'The last scan found nothing to clean up.') : 'Run a scan to look for duplicates, noise and other chores.'"
        />
        <div v-else class="flex flex-col gap-4">
          <p class="text-sm text-muted-foreground">Chores with a fix to apply, safest first. Open a kind to select several and preview them together.</p>
          <section v-for="k in fixShown" :key="k" class="surface" :aria-labelledby="`kind-${k}`">
            <header class="flex flex-col gap-2 p-4 sm:flex-row sm:items-start sm:gap-4">
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                  <h2 :id="`kind-${k}`" class="section-title">{{ labelOf(k) }}</h2>
                  <span class="text-sm font-semibold tabular-nums text-muted-foreground">{{ formatNumber(countOf(k)) }}</span>
                  <RiskBadge :risk="KIND_INFO[k].risk" />
                </div>
                <p class="mt-1 text-sm text-muted-foreground">{{ KIND_INFO[k].effect }}</p>
              </div>
              <Button variant="outline" size="sm" class="self-start" @click="openKind(k)">Review all {{ formatNumber(countOf(k)) }} <ArrowRight /></Button>
            </header>
            <div class="flex flex-col gap-2 border-t p-3 sm:p-4">
              <ListSkeleton v-if="top.get(k)?.pending" :rows="1" />
              <MaintenanceFindingCard
                v-for="f in topFixes.get(k) ?? []"
                :key="f.id"
                :finding="f"
                :kind-label="labelOf(f.kind)"
                :proposal="describe(f)"
                :into="intoOf(f)"
                :earlier="reverted.get(f.id) ?? null"
                :unchecked="unknown.get(f.id) ?? null"
                :busy="planning || Boolean(applyJobId)"
                compact
                @preview="preview([f.id])"
                @dismiss="dismiss([f.id], f.kind === 'duplicate' ? 'Not a duplicate' : 'Dismiss')"
              />
            </div>
          </section>
        </div>

        <section v-if="reviewShown.length" class="mt-6" aria-labelledby="review-title">
          <h2 id="review-title" class="section-title flex items-center gap-2"><Eye class="size-4 text-muted-foreground" aria-hidden="true" /> Review only <span class="text-sm font-semibold tabular-nums text-muted-foreground">{{ formatNumber(reviewShownTotal) }}</span></h2>
          <p class="mt-1 text-sm text-muted-foreground">Nothing to apply in bulk here: these need a person to look, or would only add AI suggestions to the inbox.</p>
          <DataTable :data="reviewRows" :columns="reviewColumns" :row-key="(r) => r.kind" row-clickable dense caption="Review-only kinds" class="mt-3" @row-click="(r) => openKind(r.kind)" />
        </section>
      </template>
    </template>

    <!-- List: one kind (or a search, or applied/dismissed/resolved findings) -->
    <template v-else>
      <div v-if="canSelect && actionable.length" class="surface mb-4 flex flex-wrap items-center gap-3 px-4 py-3">
        <label class="flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox :model-value="allSelected" :disabled="!autoSelectable.length || checking" aria-label="Select all shown" @update:model-value="(v) => toggleAll(v === true)" />
          {{ selected.size ? `${selected.size} selected` : "Select all shown" }}
        </label>
        <span v-if="checking" class="inline-flex items-center gap-1 text-xs text-muted-foreground"><Loader2 class="size-3.5 animate-spin" aria-hidden="true" /> Checking earlier merges…</span>
        <span v-if="revertedShown" class="inline-flex items-center gap-1 text-xs text-destructive">
          <Undo2 class="size-3.5" aria-hidden="true" /> {{ revertedShown }} would repeat a reverted merge: never selected for you
        </span>
        <span v-if="uncheckedShown" class="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <AlertTriangle class="size-3.5" aria-hidden="true" /> {{ uncheckedShown }} not checked for an earlier merge: never selected for you
        </span>
        <div class="ml-auto flex gap-2">
          <Button size="sm" variant="outline" :disabled="!selected.size" @click="dismiss([...selected])"><X /> Dismiss</Button>
          <Button size="sm" :disabled="!selected.size || planning || Boolean(applyJobId)" @click="preview([...selected])">
            <Loader2 v-if="planning" class="animate-spin" aria-hidden="true" /><Check v-else /> Preview changes
          </Button>
        </div>
      </div>

      <ListSkeleton v-if="findings.isPending.value" :rows="4" />
      <ErrorState v-else-if="findings.error.value" :error="findings.error.value" @retry="findings.refetch()" />
      <EmptyState
        v-else-if="items.length === 0"
        :icon="Wrench"
        :title="status === 'open' ? 'Nothing to clean up here' : 'Nothing here'"
        :description="status === 'open' ? 'The last scan found nothing for these filters.' : 'No findings with this status.'"
      />
      <template v-else>
        <ul class="flex flex-col gap-3">
          <li v-for="f in items" :key="f.id">
            <MaintenanceFindingCard
              :finding="f"
              :kind-label="labelOf(f.kind)"
              :proposal="describe(f)"
              :into="intoOf(f)"
              :earlier="reverted.get(f.id) ?? null"
              :unchecked="unknown.get(f.id) ?? null"
              :selectable="canSelect"
              :selected="selected.has(f.id)"
              :busy="planning || Boolean(applyJobId)"
              @update:selected="(on) => toggle(f.id, on)"
              @preview="preview([f.id])"
              @dismiss="dismiss([f.id], f.kind === 'duplicate' ? 'Not a duplicate' : 'Dismiss')"
              @reopen="reopen(f.id)"
              @revert="revert"
              @keep="(id) => (survivor[f.id] = id)"
            />
          </li>
        </ul>
        <div v-if="findings.hasNextPage.value" class="mt-4 flex justify-center">
          <Button variant="outline" :disabled="findings.isFetchingNextPage.value" @click="findings.fetchNextPage()">
            <Loader2 v-if="findings.isFetchingNextPage.value" class="animate-spin" aria-hidden="true" /> Load more
          </Button>
        </div>
      </template>
    </template>

    <!-- Dry run -->
    <Dialog v-model:open="planOpen">
      <DialogContent class="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Preview: {{ planApplicable }} of {{ plan?.total ?? 0 }} can be applied</DialogTitle>
          <DialogDescription>Nothing has changed yet. Each group becomes a change set with the reason shown, revertable from the change log.</DialogDescription>
        </DialogHeader>
        <div v-if="plan" class="flex flex-col gap-4 text-sm">
          <section v-if="planRepeats.length || planUnchecked.length" class="flex flex-col gap-2 rounded-lg border border-destructive/40 bg-destructive-soft p-3 text-destructive" role="alert">
            <div v-if="planRepeats.length">
              <p class="flex items-center gap-1.5 font-medium"><Undo2 class="size-4" aria-hidden="true" /> {{ planRepeats.length === 1 ? "This merge was" : `${planRepeats.length} of these merges were` }} reverted before</p>
              <ul class="mt-1 list-disc pl-5 text-xs">
                <li v-for="id in planRepeats" :key="id">
                  {{ titles.get(id) ?? `#${id}` }}: merged {{ formatDate(reverted.get(id)?.mergedAt) }} by {{ reverted.get(id)?.mergedBy }}<template v-if="reverted.get(id)?.undoneAt">, undone {{ formatDate(reverted.get(id)?.undoneAt) }} by {{ reverted.get(id)?.undoneBy }}</template>
                </li>
              </ul>
            </div>
            <div v-if="planUnchecked.length">
              <p class="flex items-center gap-1.5 font-medium"><AlertTriangle class="size-4" aria-hidden="true" /> {{ planUnchecked.length === 1 ? "One merge" : `${planUnchecked.length} merges` }} could not be checked for an earlier revert</p>
              <ul class="mt-1 list-disc pl-5 text-xs">
                <li v-for="id in planUnchecked" :key="id">{{ titles.get(id) ?? `#${id}` }}: {{ unknown.get(id) ?? "not checked" }}</li>
              </ul>
            </div>
            <label class="flex items-start gap-2 text-foreground">
              <Checkbox :model-value="repeatOk" class="mt-0.5" @update:model-value="(v) => (repeatOk = v === true)" />
              <span>{{ planRepeats.length ? "I checked: they are the same thing after all. Merge again." : "I checked the histories: merge them." }}</span>
            </label>
          </section>
          <p v-else-if="planHasMerges" class="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
            Merges move facts, relations and names into the entry that stays; the other becomes an alias. A revert restores the entry, but connector syncs may already have written to the survivor.
          </p>
          <section v-for="g in plan.groups" :key="g.op" class="rounded-lg border p-3">
            <p class="font-medium">{{ g.label }} ({{ g.items.length }})</p>
            <p class="text-xs text-muted-foreground">{{ g.reason }}</p>
            <ul class="mt-2 flex flex-col gap-1">
              <li
                v-for="it in g.items.slice(0, 30)"
                :key="it.findingId"
                :class="cn('flex flex-wrap items-center gap-x-2 gap-y-0.5', g.errors.some((e) => e.findingId === it.findingId) && 'text-destructive line-through')"
              >
                <span class="min-w-0">{{ it.description }}</span>
                <ConfidenceBadge v-if="findingById.get(it.findingId)" :value="findingById.get(it.findingId)!.confidence" />
                <span v-if="reverted.has(it.findingId)" class="inline-flex items-center gap-1 text-xs text-destructive"><Undo2 class="size-3" aria-hidden="true" /> reverted before</span>
              </li>
              <li v-if="g.items.length > 30" class="text-muted-foreground">… and {{ g.items.length - 30 }} more</li>
            </ul>
            <p v-if="g.changes.length" class="mt-2 text-xs text-muted-foreground">{{ g.changes.length }} page revision(s): {{ [...new Set(g.changes.map((c) => c.name))].slice(0, 8).join(", ") }}</p>
            <ul v-if="g.errors.length" class="mt-2 list-disc pl-5 text-xs text-destructive">
              <li v-for="e in g.errors" :key="e.findingId">{{ titles.get(e.findingId) ?? `#${e.findingId}` }}: {{ e.error }}</li>
            </ul>
            <ul v-if="g.warnings.length" class="mt-2 list-disc pl-5 text-xs text-muted-foreground">
              <li v-for="w in g.warnings.slice(0, 10)" :key="w">{{ w }}</li>
            </ul>
          </section>
          <section v-if="plan.skipped.length" class="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
            <p class="font-medium">Skipped ({{ plan.skipped.length }})</p>
            <ul class="list-disc pl-5">
              <li v-for="s in plan.skipped.slice(0, 20)" :key="s.findingId">{{ s.title || `#${s.findingId}` }}: {{ s.reason }}</li>
            </ul>
          </section>
          <label class="flex flex-col gap-1 text-sm">
            <span class="text-muted-foreground">Note for the history (optional)</span>
            <Input v-model="applyReason" placeholder="e.g. quarterly cleanup" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="planOpen = false">Cancel</Button>
          <Button :disabled="Boolean(blocked)" :title="blocked ?? undefined" @click="apply">
            <Loader2 v-if="planMerges.pending.length" class="animate-spin" aria-hidden="true" /><Check v-else /> Apply {{ planApplicable }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
